"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SigningApi = exports.FINISHED_JOB_STATUSES = void 0;
/**
 * Wywołania serwera dla podpisu kwalifikowanego pism (pack SIG, SIG-4).
 *
 * Typy odpowiedzi odwzorowują kontrakt serwera: PS-nodeJS/src/signing/jobs/SigningJobsController.ts
 * (SignableFilesResult, JobStatusResult, ManualUploadResult, SignatureSummaryResult). Źródłem prawdy
 * jest kod serwera — tu tylko tyle, ile potrzebuje interfejs.
 *
 * Bez ponawiania żądań (ToolsFetch.fetchJsonWithSafeError, nie fetchWithRetry): zakładanie zlecenia
 * i wgrywanie pliku zapisują dane, a odpytywanie stanu ponawia się samo co chwilę.
 */
const MainSetupReact_1 = __importDefault(require("../React/MainSetupReact"));
const ToolsFetch_1 = __importDefault(require("../React/Tools/ToolsFetch"));
exports.FINISHED_JOB_STATUSES = ["done", "cancelled", "expired", "failed"];
const JSON_HEADERS = { "Content-Type": "application/json" };
function url(path) {
    return MainSetupReact_1.default.serverUrl + path;
}
exports.SigningApi = {
    listSignableFiles(letterId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`letter/${letterId}/signableFiles`), {
            credentials: "include",
        });
    },
    createJob(letterId, files) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url("signingJob"), {
            method: "POST",
            credentials: "include",
            headers: JSON_HEADERS,
            body: JSON.stringify({ letterId, files }),
        });
    },
    getJobState(jobId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`signingJob/${jobId}`), {
            credentials: "include",
            cache: "no-store",
        });
    },
    cancelJob(jobId, reason) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`signingJob/${jobId}/cancel`), {
            method: "POST",
            credentials: "include",
            headers: JSON_HEADERS,
            body: JSON.stringify({ reason }),
        });
    },
    /** Adres zamrożonego PDF-a do obejrzenia w przeglądarce (nowa karta, wbudowany podgląd PDF). */
    previewUrl(jobId, fileIndex) {
        return url(`signingJob/${jobId}/file/${fileIndex}/preview`);
    },
    /** Adres instalatora programu ENVI Podpis (serwer oddaje plik jako załącznik). */
    programDownloadUrl() {
        return url("signing/program/download");
    },
    /** Czy instalator programu można pobrać i jaka to wersja (strona ENVI Podpis). */
    programInfo() {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url("signing/program/info"), {
            credentials: "include",
        });
    },
    listLetterSignatures(letterId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`letter/${letterId}/signatures`), {
            credentials: "include",
        });
    },
    summarizeSignatures(letterIds) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url("letters/signatureSummary"), {
            method: "POST",
            credentials: "include",
            headers: JSON_HEADERS,
            body: JSON.stringify({ letterIds }),
        });
    },
    uploadSigned(letterId, file) {
        return uploadSignedTo(`letter/${letterId}/signedPdf`, file);
    },
    // ------------------------------------------------------------------ oferta (pliki z folderu „Oferta”)
    listOfferSignableFiles(offerId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`offer/${offerId}/signableFiles`), {
            credentials: "include",
        });
    },
    createOfferJob(offerId, files) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url("signingJob"), {
            method: "POST",
            credentials: "include",
            headers: JSON_HEADERS,
            body: JSON.stringify({ offerId, files }),
        });
    },
    listOfferSignatures(offerId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`offer/${offerId}/signatures`), {
            credentials: "include",
        });
    },
    uploadSignedToOffer(offerId, file) {
        return uploadSignedTo(`offer/${offerId}/signedPdf`, file);
    },
    locateOfferGraphicPlace(offerId, gdFileId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`offer/${offerId}/signableFile/${encodeURIComponent(gdFileId)}/graphicPlace`), { credentials: "include", cache: "no-store" });
    },
    /** Obraz strony pliku oferty (PNG). Pobierany z sesją jak reszta API, dlatego jako Blob, a nie adres dla <img>. */
    offerFilePageImage(offerId, gdFileId, page) {
        return pageImage(`offer/${offerId}/signableFile/${encodeURIComponent(gdFileId)}/page/${page}`);
    },
    // ------------------------------------------------------------------ umowa ENVI (pliki z folderu „Umowa i zmiany”)
    listContractSignableFiles(contractId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`contract/${contractId}/signableFiles`), {
            credentials: "include",
        });
    },
    createContractJob(contractId, files) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url("signingJob"), {
            method: "POST",
            credentials: "include",
            headers: JSON_HEADERS,
            body: JSON.stringify({ contractId, files }),
        });
    },
    listContractSignatures(contractId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`contract/${contractId}/signatures`), {
            credentials: "include",
        });
    },
    uploadSignedToContract(contractId, file) {
        return uploadSignedTo(`contract/${contractId}/signedPdf`, file);
    },
    locateContractGraphicPlace(contractId, gdFileId) {
        return ToolsFetch_1.default.fetchJsonWithSafeError(url(`contract/${contractId}/signableFile/${encodeURIComponent(gdFileId)}/graphicPlace`), { credentials: "include", cache: "no-store" });
    },
    /** Obraz strony pliku umowy (PNG). Jak przy ofercie: Blob z sesją, a nie adres dla <img>. */
    contractFilePageImage(contractId, gdFileId, page) {
        return pageImage(`contract/${contractId}/signableFile/${encodeURIComponent(gdFileId)}/page/${page}`);
    },
};
/** Obraz strony (PNG) jako Blob z sesją. Omija fetchJsonWithSafeError, więc sesję i brak sieci obsługujemy tutaj. */
async function pageImage(path) {
    let response;
    try {
        response = await fetch(url(path), { credentials: "include", cache: "no-store" });
    }
    catch {
        throw new Error("Brak połączenia z serwerem. Spróbuj ponownie za chwilę.");
    }
    if (!response.ok) {
        if (response.status === 401)
            ToolsFetch_1.default.notifySessionExpired();
        let message = "Nie udało się pobrać obrazu strony.";
        try {
            const body = await response.json();
            if (typeof body?.errorMessage === "string")
                message = body.errorMessage;
            else if (typeof body?.message === "string")
                message = body.message;
        }
        catch {
            // odpowiedź bez JSON - zostaje komunikat ogólny
        }
        throw new Error(message);
    }
    return response.blob();
}
function uploadSignedTo(path, file) {
    const form = new FormData();
    form.append("file", file);
    // Bez nagłówka Content-Type: przeglądarka sama dopisze granicę multipart.
    return ToolsFetch_1.default.fetchJsonWithSafeError(url(path), {
        method: "POST",
        credentials: "include",
        body: form,
    });
}
